import React from 'react';
import ReactCrop, {
  centerCrop, makeAspectCrop, PixelCrop,
} from 'react-image-crop';
import { EAdCreativeMediaType } from 'types/ad-campaign';

import 'react-image-crop/dist/ReactCrop.css';

import {
  Box, ButtonBase, LinearProgress, Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined';
import MovieOutlinedIcon from '@material-ui/icons/MovieOutlined';
import ReplayIcon from '@material-ui/icons/Replay';

const IMAGE_MAX_BYTES = 30 * 1024 * 1024;
const VIDEO_MAX_BYTES = 250 * 1024 * 1024;
const MIN_DIMENSION = 1080;
const RECOMMENDED_DIMENSION = 1440;

const useStyles = makeStyles((theme) => ({
  mediaTypeButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
  },
  mediaTypeButton: {
    border: `1px solid ${theme.palette.grayScale4.main}`,
    borderRadius: '8px',
    padding: '9px 14px',
    color: theme.palette.darkGray.main,
    fontWeight: 600,
    transition: 'border-color 180ms ease, background-color 180ms ease',
    '& svg': {
      marginRight: '7px',
    },
    '&:focus-visible': {
      outline: `3px solid ${theme.palette.orange.main}`,
      outlineOffset: '2px',
    },
  },
  mediaTypeButtonSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.offWhite.main,
    color: theme.palette.primary.main,
  },
  dropzone: {
    minHeight: '190px',
    width: '100%',
    border: `2px dashed ${theme.palette.grayScale4.main}`,
    borderRadius: '10px',
    backgroundColor: theme.palette.offWhite.main,
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 180ms ease, background-color 180ms ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&:focus-visible': {
      outline: `3px solid ${theme.palette.orange.main}`,
      outlineOffset: '2px',
    },
    '& svg': {
      color: theme.palette.primary.main,
      fontSize: '42px',
      marginBottom: '8px',
    },
  },
  dropzoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.white.main,
  },
  hiddenInput: {
    display: 'none',
  },
  requirements: {
    marginTop: '8px',
    color: theme.palette.lightGray.main,
    fontSize: '14px',
    lineHeight: '20px',
  },
  guidance: {
    margin: '12px 0 14px',
    color: theme.palette.darkGray.main,
    fontSize: '14px',
    lineHeight: '21px',
  },
  cropContainer: {
    backgroundColor: theme.palette.grayScale6.main,
    borderRadius: '10px',
    padding: '12px',
    display: 'flex',
    justifyContent: 'center',
    '& img': {
      maxHeight: '520px',
      maxWidth: '100%',
    },
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginTop: '12px',
  },
  actionButton: {
    borderRadius: '8px',
    padding: '9px 14px',
    fontWeight: 600,
    border: `1px solid ${theme.palette.grayScale4.main}`,
    '& svg': {
      marginRight: '6px',
    },
    '&:focus-visible': {
      outline: `3px solid ${theme.palette.orange.main}`,
      outlineOffset: '2px',
    },
  },
  primaryAction: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.white.main,
  },
  status: {
    minHeight: '22px',
    marginTop: '10px',
    fontSize: '14px',
    lineHeight: '20px',
  },
  error: {
    color: theme.palette.error.main,
  },
  warning: {
    color: theme.palette.orange.main,
  },
  progress: {
    marginTop: '14px',
  },
}));

interface Props {
  mediaType: EAdCreativeMediaType;
  value?: File;
  error?: string;
  uploadProgress?: number;
  uploadError?: string;
  onMediaTypeChange: (mediaType: EAdCreativeMediaType) => void;
  onChange: (file?: File) => void;
  onPreviewUrlChange: (url?: string) => void;
  onRetry?: () => void;
}

const getInitialCrop = (width: number, height: number) => centerCrop(
  makeAspectCrop(
    {
      unit: '%',
      width: 100,
    },
    1,
    width,
    height,
  ),
  width,
  height,
);

export default ({
  mediaType,
  value,
  error,
  uploadProgress,
  uploadError,
  onMediaTypeChange,
  onChange,
  onPreviewUrlChange,
  onRetry,
}: Props) => {
  const classes = useStyles();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const imageRef = React.useRef<HTMLImageElement>(null);
  const sourceUrlRef = React.useRef<string>();
  const previewUrlRef = React.useRef<string>();

  const [sourceUrl, setSourceUrl] = React.useState<string>();
  const [crop, setCrop] = React.useState<any>();
  const [completedCrop, setCompletedCrop] = React.useState<PixelCrop>();
  const [message, setMessage] = React.useState<string>();
  const [messageIsError, setMessageIsError] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);

  const revokeUrl = (url?: string) => {
    if (url) URL.revokeObjectURL(url);
  };

  const reset = React.useCallback(() => {
    revokeUrl(sourceUrlRef.current);
    revokeUrl(previewUrlRef.current);
    sourceUrlRef.current = undefined;
    previewUrlRef.current = undefined;
    setSourceUrl(undefined);
    setCrop(undefined);
    setCompletedCrop(undefined);
    setMessage(undefined);
    setMessageIsError(false);
    onChange(undefined);
    onPreviewUrlChange(undefined);
    if (inputRef.current) inputRef.current.value = '';
  }, [onChange, onPreviewUrlChange]);

  React.useEffect(() => () => {
    revokeUrl(sourceUrlRef.current);
    revokeUrl(previewUrlRef.current);
  }, []);

  const showMessage = (nextMessage: string, isErrorMessage = false) => {
    setMessage(nextMessage);
    setMessageIsError(isErrorMessage);
  };

  const selectImage = (file: File) => {
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      showMessage('Choose a JPG or PNG image.', true);
      return;
    }
    if (file.size > IMAGE_MAX_BYTES) {
      showMessage('Images must be 30 MB or smaller.', true);
      return;
    }

    reset();
    const url = URL.createObjectURL(file);
    sourceUrlRef.current = url;
    setSourceUrl(url);
    showMessage('Adjust the square crop, then confirm it.');
  };

  const selectVideo = (file: File) => {
    const inferredMimeType = file.type || (
      file.name.toLowerCase().endsWith('.mov') ? 'video/quicktime' : ''
    );
    if (!['video/mp4', 'video/quicktime'].includes(inferredMimeType)) {
      showMessage('Choose an MP4 or MOV video.', true);
      return;
    }
    if (file.size > VIDEO_MAX_BYTES) {
      showMessage('Videos must be 250 MB or smaller.', true);
      return;
    }

    reset();
    const normalizedFile = file.type === inferredMimeType
      ? file
      : new File([file], file.name, {
        type: inferredMimeType,
        lastModified: file.lastModified,
      });
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      if (video.videoWidth !== video.videoHeight || video.videoWidth < MIN_DIMENSION) {
        revokeUrl(url);
        showMessage('Videos must be square and at least 1080 × 1080 pixels.', true);
        return;
      }
      if (video.duration < 3 || video.duration > 60) {
        revokeUrl(url);
        showMessage('Videos must be between 3 and 60 seconds long.', true);
        return;
      }

      previewUrlRef.current = url;
      onChange(normalizedFile);
      onPreviewUrlChange(url);
      showMessage(
        video.duration > 15
          ? 'This video is valid. Meta recommends keeping the key message early; 15 seconds or less often performs best.'
          : 'Video ready. H.264, AAC, and frame-rate checks happen after upload.',
      );
    };
    video.onerror = () => {
      revokeUrl(url);
      showMessage('We could not read this video. Choose another MP4 or MOV file.', true);
    };
    video.src = url;
  };

  const selectFile = (file?: File) => {
    if (!file) return;
    setMessage(undefined);
    setMessageIsError(false);
    if (mediaType === EAdCreativeMediaType.Image) selectImage(file);
    else selectVideo(file);
  };

  const confirmCrop = async () => {
    if (!imageRef.current || !completedCrop?.width || !completedCrop?.height) {
      showMessage('Select a square crop before continuing.', true);
      return;
    }

    const image = imageRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const outputWidth = Math.floor(completedCrop.width * scaleX);
    const outputHeight = Math.floor(completedCrop.height * scaleY);

    if (outputWidth < MIN_DIMENSION || outputHeight < MIN_DIMENSION) {
      showMessage('The selected crop must be at least 1080 × 1080 pixels.', true);
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      showMessage('Your browser could not prepare this crop. Please try another image.', true);
      return;
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      outputWidth,
      outputHeight,
    );

    const mimeType = image.dataset.mimeType === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, mimeType, mimeType === 'image/jpeg' ? 0.92 : undefined);
    });
    if (!blob) {
      showMessage('Your browser could not prepare this crop. Please try again.', true);
      return;
    }

    const extension = mimeType === 'image/png' ? 'png' : 'jpg';
    const normalizedFile = new File([blob], `ad-creative.${extension}`, {
      type: mimeType,
      lastModified: Date.now(),
    });
    if (normalizedFile.size > IMAGE_MAX_BYTES) {
      showMessage('The cropped image is still larger than 30 MB. Choose a smaller image.', true);
      return;
    }

    revokeUrl(previewUrlRef.current);
    const previewUrl = URL.createObjectURL(normalizedFile);
    previewUrlRef.current = previewUrl;
    onChange(normalizedFile);
    onPreviewUrlChange(previewUrl);
    setCrop(undefined);
    setCompletedCrop(undefined);
    showMessage(
      outputWidth < RECOMMENDED_DIMENSION
        ? `Crop confirmed at ${outputWidth} × ${outputHeight}. It is valid, but 1440 × 1440 or larger is recommended.`
        : `Crop confirmed at ${outputWidth} × ${outputHeight}.`,
    );
  };

  const hasConfirmedFile = !!value;
  // Prefer the uploader's specific validation result over Formik's generic
  // required-field message while a file is being inspected or cropped.
  const activeMessage = uploadError || message || error;
  const activeMessageIsError = !!uploadError || (message ? messageIsError : !!error);

  return (
    <div>
      <div
        className={classes.mediaTypeButtons}
        role="radiogroup"
        aria-label="Creative media type"
        aria-describedby="ad-creative-upload-guidance"
      >
        <ButtonBase
          className={`${classes.mediaTypeButton} ${mediaType === EAdCreativeMediaType.Image ? classes.mediaTypeButtonSelected : ''}`}
          role="radio"
          aria-checked={mediaType === EAdCreativeMediaType.Image}
          onClick={() => {
            if (mediaType !== EAdCreativeMediaType.Image) reset();
            onMediaTypeChange(EAdCreativeMediaType.Image);
          }}
        >
          <ImageOutlinedIcon />
          Image
        </ButtonBase>
        <ButtonBase
          className={`${classes.mediaTypeButton} ${mediaType === EAdCreativeMediaType.Video ? classes.mediaTypeButtonSelected : ''}`}
          role="radio"
          aria-checked={mediaType === EAdCreativeMediaType.Video}
          onClick={() => {
            if (mediaType !== EAdCreativeMediaType.Video) reset();
            onMediaTypeChange(EAdCreativeMediaType.Video);
          }}
        >
          <MovieOutlinedIcon />
          Video
        </ButtonBase>
      </div>

      <div
        id="ad-creative-upload-guidance"
        className={classes.guidance}
        aria-live="polite"
      >
        {mediaType === EAdCreativeMediaType.Image
          ? 'Use a square image. For best results, use a high-resolution image, keep text minimal, and give the design one clear focal point.'
          : 'Use a square video. Put the key message in the first three seconds, feature a person when it fits, and pair audio with captions or on-screen meaning. This square format works across placements; Meta generally favors 4:5 in Feed and 9:16 in Stories and Reels.'}
      </div>

      <input
        ref={inputRef}
        className={classes.hiddenInput}
        type="file"
        accept={mediaType === EAdCreativeMediaType.Image ? 'image/jpeg,image/png' : 'video/mp4,video/quicktime,.mov'}
        onChange={(event) => selectFile(event.target.files?.[0])}
        aria-label={`Choose an ad creative ${mediaType.toLowerCase()}`}
      />

      {!sourceUrl && !hasConfirmedFile && (
        <div
          className={`${classes.dropzone} ${isDragging ? classes.dropzoneActive : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragEnter={(event) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setIsDragging(false);
            selectFile(event.dataTransfer.files?.[0]);
          }}
        >
          <CloudUploadOutlinedIcon />
          <Typography variant="subtitle1"><b>Drop your creative here or choose a file</b></Typography>
          <div className={classes.requirements}>
            {mediaType === EAdCreativeMediaType.Image
              ? 'JPG or PNG · square · at least 1080 × 1080 · up to 30 MB'
              : 'MP4 or MOV · H.264 · square · 3–60 seconds · up to 250 MB'}
          </div>
        </div>
      )}

      {sourceUrl && mediaType === EAdCreativeMediaType.Image && !hasConfirmedFile && (
        <>
          <div className={classes.cropContainer}>
            <ReactCrop
              crop={crop}
              onChange={(pixelCrop, percentCrop) => setCrop(percentCrop)}
              onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
              aspect={1}
              minWidth={100}
              ruleOfThirds
            >
              <img
                ref={imageRef}
                src={sourceUrl}
                alt="Crop uploaded ad creative"
                data-mime-type={inputRef.current?.files?.[0]?.type}
                onLoad={(event) => {
                  const { naturalWidth, naturalHeight } = event.currentTarget;
                  if (naturalWidth < MIN_DIMENSION || naturalHeight < MIN_DIMENSION) {
                    showMessage('Images must be at least 1080 pixels wide and tall.', true);
                    setCrop(undefined);
                    return;
                  }
                  setCrop(getInitialCrop(naturalWidth, naturalHeight));
                }}
              />
            </ReactCrop>
          </div>
          <div className={classes.actions}>
            <ButtonBase
              className={`${classes.actionButton} ${classes.primaryAction}`}
              onClick={confirmCrop}
            >
              Confirm square crop
            </ButtonBase>
            <ButtonBase className={classes.actionButton} onClick={reset}>
              <DeleteOutlineIcon />
              Remove
            </ButtonBase>
          </div>
        </>
      )}

      {hasConfirmedFile && (
        <div className={classes.actions}>
          <ButtonBase className={classes.actionButton} onClick={() => inputRef.current?.click()}>
            <CloudUploadOutlinedIcon />
            Replace
          </ButtonBase>
          <ButtonBase className={classes.actionButton} onClick={reset}>
            <DeleteOutlineIcon />
            Remove
          </ButtonBase>
        </div>
      )}

      {uploadProgress !== undefined && uploadProgress < 100 && (
        <Box className={classes.progress} aria-live="polite">
          <Typography variant="body2">{`Uploading creative: ${uploadProgress}%`}</Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}

      {uploadProgress === 100 && !uploadError && (
        <div className={classes.status} role="status" aria-live="polite">
          Upload complete. Harvist is validating the creative with Meta.
        </div>
      )}

      {activeMessage && (
        <div
          className={`${classes.status} ${activeMessageIsError ? classes.error : classes.warning}`}
          role={activeMessageIsError ? 'alert' : 'status'}
          aria-live="polite"
        >
          {activeMessage}
          {uploadError && onRetry && (
            <ButtonBase className={classes.actionButton} onClick={onRetry}>
              <ReplayIcon />
              Try again
            </ButtonBase>
          )}
        </div>
      )}
    </div>
  );
};
